import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { clearAuthStorage } from '../utils/authSession';
import MyProfileModal from './MyProfileModal';
import { usePermissions } from '../permissions/PermissionContext';
import {
  normalizeMenuId,
  pathForMenu,
  menuFromPathname,
} from '../routes/menuPaths';
import { Spinner } from './ui/Spinner';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { BrandingProvider, useBranding } from '../branding/BrandingContext';

function CompanyLogo({ className, ringClass = 'ring-2 ring-white/10', variant = 'sidebar' }) {
  const { companyName, logoUrl, loading } = useBranding();
  const sizeClass = variant === 'sidebar'
    ? 'w-11 h-11 sm:w-12 sm:h-12'
    : 'w-10 h-10 sm:w-11 sm:h-11';

  if (loading && !logoUrl) {
    return (
      <div className={`${sizeClass} ${className} rounded-lg bg-white/10 flex items-center justify-center shrink-0 ${ringClass}`}>
        <Spinner size="sm" tone={variant === 'sidebar' ? 'white' : 'primary'} />
      </div>
    );
  }

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={companyName}
        className={`${sizeClass} ${className} object-contain bg-white rounded-lg p-1 shrink-0 ${ringClass}`}
      />
    );
  }
  return (
    <div className={`${sizeClass} ${className} rounded-lg bg-gradient-to-br from-primary-400 to-secondary-500 flex items-center justify-center text-base font-bold shadow-lg shadow-primary-900/30 ${ringClass} shrink-0`}>
      {companyName.charAt(0)}
    </div>
  );
}

function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeMenu = menuFromPathname(location.pathname);
  const { companyName } = useBranding();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const saved = localStorage.getItem('sidebarOpen');
      if (saved === 'true') return true;
      if (saved === 'false') return false;
    } catch {
      // ignore
    }
    return window.innerWidth >= 1440;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    loading: permissionsLoading,
    ownerAdmin,
    accessRestricted,
    accessMessage,
    roleName,
    canViewMenu,
  } = usePermissions();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [showMyProfile, setShowMyProfile] = useState(false);
  const profileMenuRef = useRef(null);

  /** Keep legacy `activeMenu` in sync for any code that still reads localStorage. */
  useEffect(() => {
    try {
      localStorage.setItem('activeMenu', activeMenu);
    } catch {
      // ignore
    }
  }, [activeMenu]);

  /** One-time: restore last screen from legacy localStorage when URL is still `/`. */
  useEffect(() => {
    if (permissionsLoading || accessRestricted) {
      return;
    }
    try {
      const saved = normalizeMenuId(localStorage.getItem('activeMenu') || 'Dashboard');
      if (saved === 'Dashboard' && !ownerAdmin) {
        return;
      }
      if (saved !== 'Dashboard' && location.pathname === '/' && canViewMenu(saved)) {
        navigate(pathForMenu(saved), { replace: true });
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsLoading, accessRestricted, ownerAdmin]);

  /** Expand sidebar groups that contain the current route. */
  useEffect(() => {
    const p = location.pathname;
    setExpandedMenus((prev) => {
      const next = { ...prev };
      if (p.startsWith('/masters/')) next['Feeders & Colors'] = true;
      if (p.startsWith('/insights/')) next['Category Insights'] = true;
      if (p.startsWith('/users/')) next.Users = true;
      if (p.startsWith('/security/')) next.Permissions = true;
      return next;
    });
  }, [location.pathname]);

  useEffect(() => {
    if (activeMenu === 'Production' || activeMenu === 'KHATA') {
      navigate(pathForMenu('Production Units'), { replace: true });
    }
  }, [activeMenu, navigate]);

  useEffect(() => {
    if (!showProfileMenu) {
      return;
    }
    const handlePointerDown = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [showProfileMenu]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('sidebarOpen', String(sidebarOpen));
    } catch {
      // ignore
    }
  }, [sidebarOpen]);

  const handleLogout = () => {
    clearAuthStorage();
    try {
      localStorage.removeItem('activeMenu');
    } catch {
      // ignore
    }
    try {
      localStorage.setItem('app:globalLogout', String(Date.now()));
    } catch {
      // ignore
    }
    window.location.href = '/login';
  };

  const handleMenuChange = useCallback(
    (menuId) => {
      const id = normalizeMenuId(menuId);
      try {
        localStorage.setItem('activeMenu', id);
      } catch {
        // ignore
      }
      navigate(pathForMenu(id));
      setMobileMenuOpen(false);
    },
    [navigate]
  );

  const handleProfileUpdated = (profileData) => {
    const updatedUser = {
      ...user,
      username: profileData.username || user.username,
      email: profileData.email || user.email,
      mobile: profileData.mobile || user.mobile,
      profileImage: profileData.profileImage || user.profileImage,
      systemRole: profileData.systemRole || user.systemRole,
    };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const menuItems = [
    { id: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { id: 'Sales', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { id: 'Orders', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
    { id: 'Stock', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
    { id: 'Products', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
    { id: 'Designs', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    {
      id: 'Feeders & Colors',
      icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
      subItems: ['Feeders', 'Colors', 'Production Units'],
    },
    {
      id: 'Category Insights',
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      subItems: ['Categories', 'Sub Categories', 'Concepts'],
    },
    {
      id: 'Users',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      subItems: ['Admin User', 'Brokers', 'Parties', 'Butta Cutting Parties', 'Mill Finish Parties', 'Border Cutting Parties', 'Handwork Parties', 'Transport parties'],
    },
    {
      id: 'Permissions',
      icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
      subItems: ['Roles', 'Assign Roles'],
    },
    { id: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
  ];

  const filteredMenuItems = menuItems
    .map((item) => {
      if (!item.subItems) {
        return item;
      }
      if (item.id === 'Users' || item.id === 'Permissions') {
        if (!ownerAdmin) {
          return { ...item, subItems: [] };
        }
        return item;
      }
      const filteredSubItems = item.subItems.filter((subItem) => canViewMenu(subItem));
      return { ...item, subItems: filteredSubItems };
    })
    .filter((item) => {
      if (accessRestricted) {
        return false;
      }
      if (item.subItems) {
        if (item.id === 'Users' || item.id === 'Permissions') {
          return ownerAdmin && item.subItems.length > 0;
        }
        return item.subItems.length > 0;
      }
      return canViewMenu(item.id);
    });

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const getSubItemIcon = (subItem) => {
    const iconMap = {
      Feeders: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
      Colors: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
      Categories: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
      'Sub Categories': 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
      Concepts: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      'Admin User': 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      Brokers: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      Parties: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      'Butta Cutting Parties': 'M5 13l4 4L19 7',
      'Mill Finish Parties': 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
      'Border Cutting Parties': 'M4 7h16M4 12h10M4 17h7',
      'Handwork Parties': 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
      'Production Units': 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      'Transport parties': 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
      Roles: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      'Assign Roles': 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
    };
    return iconMap[subItem] || 'M9 5l7 7-7 7';
  };

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100/90">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-100/90 overflow-hidden">
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/45 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
        bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white
        transition-all duration-300 ease-in-out
        fixed lg:static inset-y-0 left-0
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarOpen ? 'w-56' : 'w-20'}
        flex flex-col shadow-xl z-50 lg:z-30 border-r border-white/5
      `}
      >
        <div className={`border-b border-white/10 ${sidebarOpen ? 'px-4 py-3.5' : 'p-3'}`}>
          <div className={`flex items-center ${sidebarOpen ? 'gap-2.5' : 'justify-center'}`}>
            <CompanyLogo variant="sidebar" className="rounded-lg" />
            {sidebarOpen && (
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white tracking-tight truncate">
                  {companyName}
                </p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
          {sidebarOpen && (
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">
              Main menu
            </p>
          )}
          {filteredMenuItems.map((item) => {
            const isParentActive = item.subItems
              ? item.subItems.includes(activeMenu)
              : activeMenu === item.id;
            return (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={() => (item.subItems ? toggleMenu(item.id) : handleMenuChange(item.id))}
                  title={!sidebarOpen ? item.id : undefined}
                  className={`
                    group w-full flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'} px-2.5 py-2 rounded-lg
                    transition-all duration-200 ease-swift relative
                    ${isParentActive
                      ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-900/30'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  {isParentActive && sidebarOpen && (
                    <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-white/90" aria-hidden="true" />
                  )}
                  <div className="flex items-center space-x-3 min-w-0">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {sidebarOpen && <span className="text-[0.8125rem] font-medium leading-snug truncate">{item.id}</span>}
                  </div>
                  {item.subItems && sidebarOpen && (
                    <svg className={`w-4 h-4 transition-transform duration-200 ${expandedMenus[item.id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {item.subItems && expandedMenus[item.id] && sidebarOpen && (
                  <div className="ml-4 mt-1 pl-3 border-l border-white/10 space-y-0.5 overflow-hidden animate-slide-down">
                    {item.subItems.map((subItem) => (
                      <button
                        type="button"
                        key={subItem}
                        onClick={() => handleMenuChange(subItem)}
                        className={`
                          w-full flex items-center px-3 py-2 rounded-lg text-sm
                          transition-all duration-200 ease-swift
                          ${activeMenu === subItem
                            ? 'bg-primary-600/90 text-white shadow-md'
                            : 'text-slate-400 hover:bg-white/10 hover:text-white'
                          }
                        `}
                      >
                        <svg className="w-4 h-4 flex-shrink-0 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getSubItemIcon(subItem)} />
                        </svg>
                        <span className="truncate">{subItem}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center p-1.5 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
            </svg>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white/90 backdrop-blur-md border-b border-slate-200/80 px-3 sm:px-4 lg:px-4 py-2 shadow-sm z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <CompanyLogo variant="header" className="rounded-md" ringClass="ring-1 ring-slate-200" />
              <h1 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">{companyName}</h1>
            </div>

            <div className="flex items-center space-x-2 lg:space-x-4">
              <div className="relative" ref={profileMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100/80 transition-colors"
                >
                  {user.profileImage ? (
                    <AuthenticatedImage src={user.profileImage} alt="Profile" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-xs text-white font-semibold ring-2 ring-white shadow-sm">
                      {(user.username || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900">{user.username || 'User'}</p>
                    <p className="text-xs text-slate-500">
                      {accessRestricted
                        ? 'No role assigned'
                        : ownerAdmin
                          ? 'Owner Admin'
                          : roleName || user.email || 'user@example.com'}
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-large border border-slate-200/80 py-2 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                      <p className="text-sm font-medium text-slate-900">{user.username || 'User'}</p>
                      <p className="text-xs text-slate-500">{user.email || 'user@example.com'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProfileMenu(false);
                        setShowMyProfile(true);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-primary-50/80 transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>My Profile</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-3 pt-0 sm:px-4 sm:pb-4 lg:px-4 lg:pb-4 xl:px-5 xl:pb-5 2xl:px-6 2xl:pb-6 text-slate-800 text-[0.9375rem] leading-normal">
          <div
            id="dashboard-main-scroll"
            className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-3 sm:pt-4 lg:pt-4 xl:pt-5 2xl:pt-6"
          >
            <Outlet />
          </div>
        </main>
      </div>

      {showMyProfile && (
        <MyProfileModal
          onClose={() => setShowMyProfile(false)}
          onProfileUpdated={handleProfileUpdated}
        />
      )}
    </div>
  );
}

function Dashboard() {
  return (
    <BrandingProvider>
      <DashboardLayout />
    </BrandingProvider>
  );
}

export default Dashboard;
